import React, { useCallback, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  LayoutChangeEvent,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { Text } from '@/components/common/text';
import { CarouselDots } from '@/components/residents/marketplace/CarouselDots';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { FadeInImage } from '@/components/shared/redesign/FadeInImage';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';

interface MaintenanceImageCarouselProps {
  images: string[];
  variant?: 'mobile' | 'desktop';
}

export const MaintenanceImageCarousel: React.FC<MaintenanceImageCarouselProps> = ({
  images,
  variant = 'mobile'
}) => {
  const { colors } = useThemeColors();
  const styles = useCarouselStyles();
  const reduceMotion = useReducedMotion();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const scrollRef = useRef<ScrollView>(null);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setPageWidth(e.nativeEvent.layout.width);
  }, []);

  const handleMomentumEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (pageWidth <= 0) return;
      const page = Math.round(e.nativeEvent.contentOffset.x / pageWidth);
      setCurrentImageIndex(Math.max(0, Math.min(page, images.length - 1)));
    },
    [pageWidth, images.length],
  );

  const handleDotPress = useCallback(
    (index: number) => {
      setCurrentImageIndex(index);
      scrollRef.current?.scrollTo({ x: index * pageWidth, animated: !reduceMotion });
    },
    [pageWidth, reduceMotion],
  );

  // Desktop shows a placeholder when no images
  if (images.length === 0 && variant === 'desktop') {
    return (
      <View style={styles.noImagePlaceholder}>
        <RESIDENTS_SVG_COMPONENTS.MAINTENANCE width={48} height={48} color={colors.text.dimmer} />
        <Text style={styles.noImageText}>No images attached</Text>
      </View>
    );
  }

  // Mobile shows nothing when no images
  if (images.length === 0) {
    return null;
  }

  if (variant === 'mobile') {
    // Width lands with the first layout pass; the 1px placeholder width only
    // exists for that single pre-layout frame.
    const pageStyle = { width: pageWidth || 1, height: '100%' as const };
    return (
      <View style={styles.imageContainer}>
        <View
          style={[styles.imagePreview, styles.mobileHeight]}
          onLayout={handleLayout}
          testID="maintenance-carousel"
        >
          <ScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onMomentumScrollEnd={handleMomentumEnd}
          >
            {images.map((uri, index) => (
              <FadeInImage
                key={`${uri}-${index}`}
                uri={uri}
                style={pageStyle}
                accessibilityLabel={`Photo ${index + 1} of ${images.length}`}
              />
            ))}
          </ScrollView>
        </View>
        {images.length > 1 && (
          <View style={styles.dotsBar}>
            {/* `showBackground={false}` because `dotsBar` is already an opaque
                themed surface — the pill CarouselDots draws otherwise is for
                dots sitting on a photo, and would just be chrome inside a bar.
                The dot colors are left to CarouselDots' defaults, which resolve
                to these same `components.carousel` tokens. */}
            <CarouselDots
              count={images.length}
              activeIndex={currentImageIndex}
              onDotPress={handleDotPress}
              showBackground={false}
            />
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={styles.imageContainer}>
      <View style={[styles.imagePreview, styles.desktopHeight]}>
        {/* Keyed by page so each switch re-fades over the placeholder tone. */}
        <FadeInImage
          key={images[currentImageIndex]}
          uri={images[currentImageIndex]}
          style={staticStyles.fill}
          accessibilityLabel={`Photo ${currentImageIndex + 1} of ${images.length}`}
        />
      </View>
      {images.length > 1 && (
        <View style={styles.dotsBar}>
          <CarouselDots
            count={images.length}
            activeIndex={currentImageIndex}
            onDotPress={setCurrentImageIndex}
            showBackground={false}
          />
          <TouchableOpacity
            style={staticStyles.imageNav}
            onPress={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Next photo"
          >
            <Text style={styles.imageNavText}>NEXT</Text>
            <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={20} height={20} color={colors.text.muted} />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

// Static styles that don't depend on theme
const staticStyles = StyleSheet.create({
  fill: {
    width: '100%',
    height: '100%',
  },
  imageNav: {
    position: 'absolute',
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
});

const useCarouselStyles = () =>
  useThemedStyles(({ colors }) => ({
    imageContainer: {
      borderRadius: 8,
      overflow: 'hidden' as const,
    },
    imagePreview: {
      backgroundColor: colors.card.bg,
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
    mobileHeight: {
      height: 281,
    },
    desktopHeight: {
      height: 302,
    },
    dotsBar: {
      backgroundColor: colors.card.bg,
      borderTopWidth: 1,
      borderTopColor: colors.card.border,
      height: 48,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      paddingHorizontal: 16,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    imageNavText: {
      fontSize: 14,
      fontWeight: '500' as const,
      color: colors.text.muted,
    },
    noImagePlaceholder: {
      height: 302,
      backgroundColor: colors.card.bg,
      borderRadius: 8,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      gap: 12,
    },
    noImageText: {
      fontSize: 14,
      color: colors.text.dimmer,
    },
  }));
